import axios from "axios";
import { getApiBaseUrl } from "../../config/api";
import {
    getRequest,
    getSchemesSuccess,
    getSchemeSuccess,
    getLessonsSuccess,
    getLessonSuccess,
    getAssignmentsSuccess,
    getClassAssignmentsSuccess,
    getLeavesSuccess,
    getStatsSuccess,
    stuffDone,
    getFailed,
    getError,
} from "./teacherToolsSlice";

function buildQuery(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            params.append(key, value);
        }
    });
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

function isFailurePayload(data) {
    if (!data?.message) return false;
    if (data.id || data._id) return false;
    if (Array.isArray(data)) return false;
    if (data.list) return false;
    const successOnly = ["Scheme deleted", "Lesson plan deleted", "Assignment deleted"];
    if (successOnly.includes(data.message)) return false;
    return true;
}

function dispatchStuffDoneOrFailed(dispatch, data) {
    if (isFailurePayload(data)) {
        dispatch(getFailed(data.message));
    } else {
        dispatch(stuffDone());
    }
}

const EMPTY_LIST_MESSAGES = new Set([
    "No schemes found",
    "No lesson plans found",
    "No assignments",
    "No leave applications found",
]);

function dispatchListSuccess(dispatch, data, successAction) {
    if (Array.isArray(data)) {
        dispatch(successAction(data));
        return;
    }
    if (data?.list) {
        dispatch(successAction(data.list));
        return;
    }
    if (data?.message && EMPTY_LIST_MESSAGES.has(data.message)) {
        dispatch(successAction([]));
        return;
    }
    if (data?.message && !data?.id && !data?._id) {
        dispatch(getFailed(data.message));
        return;
    }
    dispatch(successAction(Array.isArray(data) ? data : []));
}

// ── Schemes ──────────────────────────────────────────────────────────

export const createScheme = (data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(`${getApiBaseUrl()}/Scheme`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getMySchemes = (teacherId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(
            `${getApiBaseUrl()}/Schemes/teacher/${teacherId}${buildQuery(filters)}`
        );
        dispatchListSuccess(dispatch, result.data, getSchemesSuccess);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getSchemeById = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(`${getApiBaseUrl()}/Scheme/${id}`);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSchemeSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const updateSchemeWeek = (id, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/SchemeWeek/${id}`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const submitScheme = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/Scheme/${id}/submit`);
        dispatchStuffDoneOrFailed(dispatch, result.data);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const deleteScheme = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.delete(`${getApiBaseUrl()}/Scheme/${id}`);
        dispatchStuffDoneOrFailed(dispatch, result.data);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

// ── Lesson plans ─────────────────────────────────────────────────────

export const createLessonPlan = (data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(`${getApiBaseUrl()}/LessonPlan`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getMyLessonPlans = (teacherId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(
            `${getApiBaseUrl()}/LessonPlans/teacher/${teacherId}${buildQuery(filters)}`
        );
        dispatchListSuccess(dispatch, result.data, getLessonsSuccess);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getLessonPlanById = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(`${getApiBaseUrl()}/LessonPlan/${id}`);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getLessonSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const updateLessonPlan = (id, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/LessonPlan/${id}`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const deleteLessonPlan = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.delete(`${getApiBaseUrl()}/LessonPlan/${id}`);
        dispatchStuffDoneOrFailed(dispatch, result.data);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

// ── Assignments ──────────────────────────────────────────────────────

export const createAssignment = (data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(`${getApiBaseUrl()}/Assignment`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getMyAssignments = (teacherId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(
            `${getApiBaseUrl()}/Assignments/teacher/${teacherId}${buildQuery(filters)}`
        );
        dispatchListSuccess(dispatch, result.data, getAssignmentsSuccess);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getClassAssignments = (classId) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(`${getApiBaseUrl()}/Assignments/class/${classId}`);
        dispatchListSuccess(dispatch, result.data, getClassAssignmentsSuccess);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const updateAssignment = (id, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/Assignment/${id}`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const deleteAssignment = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.delete(`${getApiBaseUrl()}/Assignment/${id}`);
        dispatchStuffDoneOrFailed(dispatch, result.data);
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

// ── Leave ────────────────────────────────────────────────────────────

export const applyLeave = (data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(`${getApiBaseUrl()}/LeaveApplication`, data);
        if (result.data.message && !result.data.id && !result.data._id) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

export const getMyLeaves = (teacherId) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(`${getApiBaseUrl()}/Leaves/teacher/${teacherId}`);
        if (result.data.message && !result.data.id && !result.data._id && !result.data.list) {
            dispatch(getFailed(result.data.message));
        } else if (result.data.list) {
            dispatch(getLeavesSuccess(result.data.list));
        } else {
            dispatch(getLeavesSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};

// ── Stats ────────────────────────────────────────────────────────────

export const getTeacherStats = (teacherId) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(`${getApiBaseUrl()}/TeacherStats/${teacherId}`);
        dispatch(getStatsSuccess(result.data));
    } catch (error) {
        dispatch(getError(error.response?.data?.message || error.message));
    }
};
